import {
  call,
  put,
  takeEvery,
  takeLatest,
  cancel,
  select,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { initialize } from 'redux-form';
import moment from 'moment';

import { sortBy } from 'lodash';
import {
  REPORTS_INVOICES_PAGE_REQUEST,
  REPORTS_INVOICES_LIST_INVOICE_REQUEST,
  REPORTS_INVOICES_CHANGE_DATE,
  REPORTS_INVOICES_CHANGE_OUTLET,
  REPORTS_INVOICES_RECEIPT_REQUEST,
  REPORTS_INVOICES_HIDE_RECEIPT,
  REPORTS_INVOICES_RECORD_PAYMENT_REQUEST,
  REPORTS_INVOICES_RESEND_EMAIL,
  REPORTS_INVOICES_PRINT_RECEIPT_PDF,
  REPORTS_INVOICES_ISSUE_REFUND_REQUEST,
  REPORTS_INVOICES_ISSUE_OLD_REFUND_REQUEST,
  REPORTS_INVOICES_EXPORT_INVOICES,
  REPORTS_INVOICES_CHANGE_STATUS,
  REPORTS_INVOICES_ON_CLICK_RECORD_PAYMENT,
  REPORTS_INVOICES_ON_CANCEL_RECORD_PAYMENT,
} from '../../actions/reports/invoices/constants';
import { LOGOUT } from '../../actions/constants';
import {
  listInvoiceRequest,
  setListInvoice,
  setReceiptInvoices,
  hideCancelInvoiceModal,
  hideReceiptInvoice,
  hideOldCancelInvoiceModal,
  hideRecordPaymentModal,
  setOutlets,
  showRecordPaymentModal,
  setCurrentPage,
  setTotalPage,
} from '../../actions/reports/invoices';
import { invoicesPageRequest } from '../../actions/reports/invoicesv2';
import * as actActionBar from '../../actions/actionBar';
import * as actGetExportedFile from '../../actions/get-exported-file';
import * as actNotif from '../../actions/notifications';
import * as act from '../../actions';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as apiOutlets from '../../api/outlets';
import * as api from '../../api/invoices';
import * as invoicesSearchParamSelectors from '../../selectors/invoices/search-params';
import {
  DEFAULT_START_HOUR,
  DEFAULT_END_HOUR,
  getFormattedTime,
} from '../../utils/hourFilter';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapReportsInvoices from '../../clevertap/reports/invoices';
import { postOldRefund, postRefundV2 } from '../api/refunds';
import { getFilteredDateAsMoment } from '../api/get-filtered-date';
import {
  getInvoices,
  getReceiptInvoices,
  resendEmail,
  createRecordPayment,
} from '../api/invoices';

function* fillForm(outletIds, startDate, endDate) {
  const allDay = moment(endDate).format('HH:mm') === '23:59';
  const startHour = allDay
    ? DEFAULT_START_HOUR
    : moment(startDate).format('HH:mm');
  const endHour = allDay ? DEFAULT_END_HOUR : moment(endDate).format('HH:mm');

  yield put(
    initialize('search', {
      outlet: outletIds,
      date: { startDate, endDate, startHour, endHour, allDay },
    })
  );
  yield put(actActionBar.setSelectedOutletIds(outletIds));
}

function closeRightColumn() {
  const toggledClass = 'col-sm-12 col-md-6 col-xs-6';
  $('#mk-col-left, #mk-col-right').removeClass(toggledClass);
  $('#mk-col-left').addClass('col-sm-12');
}

function insensitive(s1, s2) {
  const s1lower = s1.toLowerCase();
  const s2lower = s2.toLowerCase();
  // eslint-disable-next-line no-nested-ternary
  return s1lower > s2lower ? 1 : s1lower < s2lower ? -1 : 0;
}

type Checkouts = unknown[];

function sortCheckouts(checkouts: Checkouts) {
  const sortedSt: Record<string, Checkouts> = {};
  const sortedNoSt: Record<string, Checkouts> = {};
  const salesTypes: string[] = Object.keys(checkouts);

  salesTypes.sort(insensitive);
  for (let key = 0; key < salesTypes.length; key++) {
    const sortedCheckouts: Checkouts = sortBy(
      checkouts[salesTypes[key]],
      (c) => c.position
    );

    if (salesTypes[key] !== 'No Sales Type') {
      sortedSt[salesTypes[key]] = sortedCheckouts;
    } else {
      sortedNoSt[salesTypes[key]] = sortedCheckouts;
    }
  }

  return Object.assign(sortedSt, sortedNoSt);
}

export function* invoicesPageRequestFlow() {
  try {
    yield put(setListInvoice(null));
    yield put(showPageLoadingSpinner(true));
    // remove any previous receipt invoice
    yield put(setReceiptInvoices(null));
    // hide receipt in first load page
    yield call(closeRightColumn);

    // call date api
    const { startDate, endDate } = yield call(getFilteredDateAsMoment);
    const startDateFormatted = startDate.format('D/M/YYYY HH:mm:ss');
    const endDateFormatted = endDate.format('D/M/YYYY HH:mm:ss');

    // set outlets
    const resOutlets = yield call(apiOutlets.outlets);
    yield put(setOutlets(resOutlets.data));
    yield put(actActionBar.setOutlets(resOutlets.data));

    const outletIds = getSelectedOutletIdFromLocalStorage();

    // set form state
    yield call(fillForm, outletIds, startDate, endDate);

    yield put(
      listInvoiceRequest({
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        page: 1,
      })
    );
    clevertapReportsInvoices.reportsInvoiceStart({
      outletIds,
      startDate,
      endDate,
    });
    $('#invoice-table-tbody').animate({ scrollTop: 0 }, 'slow');
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* getInvoicesFlow(request) {
  const {
    payload: { query, previousInvoices },
  } = request;
  const start = query.startDate;
  const end = query.endDate;
  const { page, perPage, status, keyword, onChangeKeyword } = query;

  if (onChangeKeyword) {
    yield delay(300);
  }

  const resInvoices = yield call(getInvoices, {
    start,
    end,
    page,
    perPage,
    status,
    keyword,
  });
  if (!previousInvoices) yield put(setListInvoice(null));
  if (resInvoices) {
    let invoices;
    if (previousInvoices) {
      invoices = previousInvoices.concat(resInvoices.results);
    } else {
      invoices = resInvoices.results;
    }
    yield put(setListInvoice(invoices));
    yield put(setCurrentPage(page));
    yield put(setTotalPage(resInvoices.total_pages));
  }
  yield put(showPageLoadingSpinner(false));
}

export function* changeDateInvoicesFlow(request) {
  yield put(setListInvoice(null));
  yield put(showPageLoadingSpinner(true));
  yield put(setReceiptInvoices(null));
  yield call(closeRightColumn);

  const { payload } = request;
  const { startDate, endDate, statuses, keyword } = payload;

  const startDateFormatted = moment(startDate).format('D/M/YYYY HH:mm:ss');
  const endDateFormatted = moment(endDate).format('D/M/YYYY HH:mm:ss');

  yield put(
    listInvoiceRequest({
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      page: 1,
      status: statuses,
      keyword,
    })
  );
}

export function* changeOutletInvoicesFlow(request) {
  yield put(setListInvoice(null));
  yield put(showPageLoadingSpinner(true));
  yield put(setReceiptInvoices(null));
  yield call(closeRightColumn);
  // noinspection InfiniteLoopJS
  const { payload } = request;
  const { outletIds, startDate, endDate, keyword } = payload;
  setSelectedOutletIdToLocalStorage(outletIds);
  yield call(fillForm, outletIds, startDate, endDate);

  const startDateFormatted = moment(startDate).format('D/M/YYYY HH:mm:ss');
  const endDateFormatted = moment(endDate).format('D/M/YYYY HH:mm:ss');

  yield put(
    listInvoiceRequest({
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      page: 1,
      status: [],
      keyword,
    })
  );
}

export function* changeStatusInvoicesFlow(request) {
  yield put(setListInvoice(null));
  yield put(showPageLoadingSpinner(true));
  yield put(setReceiptInvoices(null));
  yield call(closeRightColumn);

  const { payload } = request;
  const { startDate, endDate, statuses, keyword } = payload;
  const startDateFormatted = moment(startDate).format('D/M/YYYY HH:mm:ss');
  const endDateFormatted = moment(endDate).format('D/M/YYYY HH:mm:ss');

  yield put(
    listInvoiceRequest({
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      page: 1,
      status: statuses,
      keyword,
    })
  );
}

export function* receiptInvoicesRequestFlow(request) {
  const { id, uuid, isMobile } = request.payload;
  const receiptInvoices = yield call(getReceiptInvoices, id, uuid);
  const searchFormValues = yield select(invoicesSearchParamSelectors.values);
  const statuses = Object.prototype.hasOwnProperty.call(
    searchFormValues,
    'statuses'
  )
    ? searchFormValues.statuses
    : []; // paid, partially_paid, cancelled, etc.
  const { startDate, endDate, startHour, endHour, allDay } =
    searchFormValues.date;
  const { formattedStart, formattedEnd } = getFormattedTime(
    startDate,
    endDate,
    startHour,
    endHour,
    allDay
  );
  clevertapReportsInvoices.reportsInvoicesSelect({
    invoice: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      selected_statuses: statuses,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      start_date: formattedStart,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      end_date: formattedEnd,
      ...receiptInvoices,
    },
  });
  const { checkouts } = receiptInvoices;
  const sorted = sortCheckouts(checkouts);
  receiptInvoices.checkouts = null;
  receiptInvoices.checkouts = sorted;
  for (let i = 0; i < receiptInvoices.payment_refunds.length; i++) {
    const checkoutsRefund = receiptInvoices.payment_refunds[i].checkouts;
    const sortedCheckouts = sortCheckouts(checkoutsRefund);
    receiptInvoices.payment_refunds[i].checkouts = null;
    receiptInvoices.payment_refunds[i].checkouts = sortedCheckouts;
  }
  yield put(setReceiptInvoices(receiptInvoices));

  // animation
  if (!isMobile) {
    if ($('#mk-col-left').hasClass('col-sm-12')) {
      $('#mk-col-left, #mk-col-right').toggleClass(
        'col-sm-12 col-md-6 col-xs-6'
      );
      setTimeout(() => {
        $('#mk-col-right').show();
        $('[data-toggle="popover"]').popover();
      }, 600);
    }
  }
}

export function* hideReceiptInvoiceFlow(request) {
  const { isMobile } = request.payload;
  yield put(setReceiptInvoices(null));

  if (!isMobile) {
    // animation
    $('#mk-col-right').hide();
    $('#mk-col-left, #mk-col-right').toggleClass('col-sm-12 col-md-6 col-xs-6');
  }
}

export function* createRecordPaymentFlow(request) {
  const { recordPayment, outletId } = request.payload.data;
  yield call(createRecordPayment, recordPayment, outletId);
  yield call(resendEmail, recordPayment.id, recordPayment.uuid);
  yield put(
    actNotif.success(
      'Record payment has been created and Invoice Email has been successfully sent'
    )
  );
  yield put(hideRecordPaymentModal());
}

export function* resendEmailFlow(request) {
  const { invoiceId, invoiceUuid } = request.payload;
  yield call(resendEmail, invoiceId, invoiceUuid);
  yield put(actNotif.success('Email has been successfully resend'));
}

export function* printReceiptInvoicePdfFlow(request) {
  const { invoiceId, invoiceUuid } = request.payload;
  yield call(api.printReceiptInvoicePdf, invoiceId, invoiceUuid);
}

export function* issueRefundFlow(request) {
  const { data } = request.payload;
  yield call(postRefundV2, data);
  yield put(hideCancelInvoiceModal());
  yield put(hideReceiptInvoice());
  yield put(invoicesPageRequest());
}

export function* issueOldRefundFlow(request) {
  const { data } = request.payload;
  yield call(postOldRefund, data);
  yield put(hideOldCancelInvoiceModal());
  yield put(hideReceiptInvoice());
  yield put(invoicesPageRequest());
}

export function* exportInvoicesFlow(request) {
  const { query, filename, outletId } = request.payload;
  try {
    yield call(api.exportInvoices, query);
    yield put(
      actGetExportedFile.getExportedFile(filename, outletId, 'invoices')
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  }
}

export function* cancelAction(cancelledTasks) {
  yield cancel(...cancelledTasks);
}

export function* onClickRecordPaymentFlow() {
  // reset form
  yield put(
    initialize('recordPaymentForm', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      amount_received: null,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      payment_type: 'cash',
      note: '',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      payment_date: moment(),
    })
  );
  yield put(showRecordPaymentModal());
}

export function* onCancelRecordPaymentFlow() {
  yield put(hideRecordPaymentModal());
}

export default function* reportsInvoicesSaga() {
  const pageReqTask = yield takeLatest(
    REPORTS_INVOICES_PAGE_REQUEST,
    invoicesPageRequestFlow
  );
  const getInvoicesTask = yield takeLatest(
    REPORTS_INVOICES_LIST_INVOICE_REQUEST,
    getInvoicesFlow
  );
  const changeDateInvoiceTask = yield takeLatest(
    REPORTS_INVOICES_CHANGE_DATE,
    changeDateInvoicesFlow
  );
  const changeOutletTask = yield takeLatest(
    REPORTS_INVOICES_CHANGE_OUTLET,
    changeOutletInvoicesFlow
  );
  const receiptInvoiceTask = yield takeLatest(
    REPORTS_INVOICES_RECEIPT_REQUEST,
    receiptInvoicesRequestFlow
  );
  yield takeLatest(REPORTS_INVOICES_HIDE_RECEIPT, hideReceiptInvoiceFlow);
  const resendEmailTask = yield takeLatest(
    REPORTS_INVOICES_RESEND_EMAIL,
    resendEmailFlow
  );
  const printReceiptInvoicePdfTask = yield takeLatest(
    REPORTS_INVOICES_PRINT_RECEIPT_PDF,
    printReceiptInvoicePdfFlow
  );
  yield takeEvery(
    REPORTS_INVOICES_RECORD_PAYMENT_REQUEST,
    createRecordPaymentFlow
  );
  yield takeEvery(REPORTS_INVOICES_ISSUE_REFUND_REQUEST, issueRefundFlow);
  yield takeEvery(
    REPORTS_INVOICES_ISSUE_OLD_REFUND_REQUEST,
    issueOldRefundFlow
  );
  const exportInvoiceTask = yield takeLatest(
    REPORTS_INVOICES_EXPORT_INVOICES,
    exportInvoicesFlow
  );
  yield takeEvery(REPORTS_INVOICES_CHANGE_STATUS, changeStatusInvoicesFlow);
  yield takeLatest(
    REPORTS_INVOICES_ON_CLICK_RECORD_PAYMENT,
    onClickRecordPaymentFlow
  );
  yield takeLatest(
    REPORTS_INVOICES_ON_CANCEL_RECORD_PAYMENT,
    onCancelRecordPaymentFlow
  );
  yield takeEvery(LOGOUT, cancelAction, [
    pageReqTask,
    getInvoicesTask,
    changeDateInvoiceTask,
    changeOutletTask,
    receiptInvoiceTask,
    resendEmailTask,
    printReceiptInvoicePdfTask,
    exportInvoiceTask,
  ]);
}
